import ph from 'posthog-js'
import * as va from '@vercel/analytics'
import * as React from 'react'
import type { TreeManager } from '@basehub/utils/tree/index'
import type { Block, BlockChange, Role } from '@basehub/data/replicache/index'
import {
  isBlockWithChildren,
  blockTypesRecord,
  isListBlock,
  isComponentBlock,
  DEFAULT_LIST_VIEW,
} from '@basehub/data/replicache/index'
import * as TreePrimitive from '~/sotano-kit/primitives/tree'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { ContextMenuActions } from './context-menu-actions'
import { getId } from '@basehub/utils/id/index'
import { BlockIcon } from '~/components/block-icon'
import { clsx } from 'clsx'
import { EyeClosedIcon, EyeOpenIcon } from '@radix-ui/react-icons'
import { getBlockDisplayData } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import { invariant } from '@epic-web/invariant'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { toast } from 'sonner'
import {
  useIsExternalVisitor,
  useIsMemberOrHigher,
  useUserRole,
} from '~/hooks-data/use-user-role'
import { Button } from '~/sotano-kit/primitives/button'
import { useTabsStore } from '../tabs/store'
import { useRepoStore } from '../repo-store'
import { getAllowedMenuActions } from '../blocks/block-wrapper-actions/permissions'
import {
  idToSourceBlockId,
  sourceBlockIdToId,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useSelectedViewControls } from '../blocks/list/views/use-selected-view'
import { getViewDefaultTitle } from '@basehub/data/replicache/schema/block/list/utils'
import {
  RepoUndoManager,
  useRepoUndoManager,
} from '~/replicache/frontend/undo-redo/manager'
import { ReorderBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/reorder'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import {
  CreateBlockParams,
  UpdateBlockParams,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { EditorMode, useEditorMode } from '../editor-mode-provider'
import {
  useWorkingTreeStore,
  useWorkingTreeStoreRoot,
} from '~/hooks-data/blocks-tree/provider'
import { Replicache } from 'replicache'
import { RepoSpaceMutators } from '@basehub/data/replicache/mutators/repo-space/mutators'
import isEqual from 'lodash/isEqual'

type DocsTreeProps = {
  blockTreeManager: TreeManager<Block, NonNullable<unknown>>
  activeKey: { label: string; id: string }
  onActiveKeyChange: (
    key: string,
    opts: { autoFocus: boolean; partiallyOpen: boolean }
  ) => void
  selectedKeys: Set<{ label: string; id: string }>
  setSelectedKeys: React.Dispatch<
    React.SetStateAction<Set<{ label: string; id: string }>>
  >
  autoFocusKey: string
  onAutoFocusKeyFocus: () => void
  className?: string
  onUnselectFavsTree: () => void
  onCreateNewBlock: (params: { blockId: string; path: string }) => void
}

export const DocsTree = ({
  className,
  blockTreeManager,
  activeKey,
  onActiveKeyChange,
  selectedKeys,
  setSelectedKeys,
  autoFocusKey,
  onAutoFocusKeyFocus,
  onUnselectFavsTree,
  onCreateNewBlock,
}: DocsTreeProps) => {
  const { data } = useRepoReplicache()
  const addTab = useTabsStore((s) => s.addTab)
  const setFocus = useRepoStore((s) => s.setFocus)
  const undoManager = useRepoUndoManager()
  const { encodedSegment } = useBranchOrCommitSegment()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)

  const isExternalVisitor = useIsExternalVisitor()
  const userRole = useUserRole()
  const editorMode = useEditorMode()
  const isMemberOrHigher = useIsMemberOrHigher()

  const { getControls: getListViewControls } = useSelectedViewControls()

  const activeBlockPath = useWorkingTreeStore((s) => {
    return (
      s.tree?.getPath(activeKey.id, {
        format: 'array',
      }) ?? []
    )
  }, isEqual)

  const renderControllerRef = React.useRef<Record<string, () => void>>({})
  // const groupsCache = React.useRef<Map<string, {}>>(new Set())
  const childrenToRenderCache = React.useRef<
    Map<string, { id: string; type: string }[] | null>
  >(new Map())
  const store = useWorkingTreeStoreRoot()

  React.useEffect(() => {
    const unsub = store.subscribe((s) => {
      const blockTreeManager = s.tree
      if (!blockTreeManager) return
      for (const [key, children] of childrenToRenderCache.current.entries()) {
        const node = blockTreeManager.getNode(key)
        if (!node) continue
        try {
          const _children =
            // yes, this is weird but it's the only reliable way i found for the children keys change to trigger a re-render here
            // now that blockTreeManager doesn't auto-rerender the whole app...
            (blockTreeManager?.getChildrenKeys(key) ?? [])
              .map((ch) => {
                const node = blockTreeManager.getNode(ch)
                if (!node) return null
                return {
                  id: node.value.id,
                  type: node.value.type,
                }
              })
              .filter(Boolean)

          if (isEqual(children, _children)) continue
          const renderFn =
            renderControllerRef.current?.[`r_${key}`] ||
            renderControllerRef.current?.[`r_${idToSourceBlockId(node.value)}`]
          renderFn?.()
        } catch (err) {
          childrenToRenderCache.current.delete(key)
          childrenToRenderCache.current.delete(idToSourceBlockId(node.value))
          // eslint-disable-next-line react-compiler/react-compiler
          delete renderControllerRef.current?.[`r_${key}`]
          // eslint-disable-next-line react-compiler/react-compiler
          delete renderControllerRef.current?.[
            `r_${idToSourceBlockId(node.value)}`
          ]
        }
      }
    })

    return () => {
      unsub()
    }
  }, [store])

  return (
    <ChildContext.Provider
      value={{
        editorMode,
        userRole,
        activeTabKey,
        undoManager: undoManager.current,
        replicache: data?.replicache,
      }}
    >
      <TreePrimitive.Tree
        treeKey="docs-tree"
        className={className}
        treeManager={blockTreeManager as TreeManager<Block | BlockChange>}
        getKey={(node) => idToSourceBlockId(node.value as Block)}
        activeKey={activeKey}
        onActiveKeyChange={({ id }, { doubleClick }) => {
          onActiveKeyChange(id, {
            autoFocus: false,
            partiallyOpen: !doubleClick,
          })
        }}
        selectedKeys={selectedKeys}
        setSelectedKeys={setSelectedKeys}
        autoFocusKey={autoFocusKey}
        onAutoFocusKeyFocus={onAutoFocusKeyFocus}
        dragAndDrop={editorMode === 'write:all' ? 'enabled' : 'disabled'}
        allowDrop={({ keyDragged, newParentKey }) => {
          const draggedBlockNode = blockTreeManager.getNode(keyDragged, {
            required: true,
          })
          const draggedBlockParentNode = draggedBlockNode.parentKey
            ? blockTreeManager.getNode(draggedBlockNode.parentKey, {
                required: true,
              })
            : null
          const dropTarget = blockTreeManager.getNode(newParentKey, {
            required: true,
          }).value

          const path = blockTreeManager.getPath(draggedBlockNode.key, {
            format: 'string',
            required: true,
          })

          const allowed = getAllowedMenuActions({
            block: draggedBlockNode.value,
            editorMode,
            parentBlock: draggedBlockParentNode?.value,
            userRole,
            droppingIntoBlock: dropTarget,
            path,
            treeManager: blockTreeManager,
          })

          return allowed.reorder
        }}
        onDragEnd={async ({ draggedItems, dropTarget }) => {
          if (!dropTarget.newParentKey) return
          const params: ReorderBlockParams = {
            rootId: blockTreeManager.root.value.id,
            blockIds: Array.from(draggedItems.keysDragged).map(({ id }) =>
              sourceBlockIdToId(id, blockTreeManager)
            ),
            targetBlockId: sourceBlockIdToId(
              dropTarget.newParentKey,
              blockTreeManager
            ),
            intendedIndex: dropTarget.intendedIndex,
            idSeed: getId(),
            isoNow: new Date().toISOString(),
          }
          const result = await data?.replicache.mutate
            .reorderBlocks(params)
            .catch((err) => {
              console.error(err)
              // eslint-disable-next-line no-console
              if (err instanceof MutatorUserError) {
                toast.error(err.reason)
              } else {
                toast.error('An error occurred')
              }
            })
          if (result?.undo) {
            undoManager.current?.push({
              redo: [{ op: 'reorderBlocks', args: params }],
              undo: [
                { op: 'blockBatchMutations', args: { mutations: result.undo } },
              ],
            })
          }
        }}
        onCreateNewChild={
          editorMode === 'write:all'
            ? async (parent, block) => {
                const parentNode = blockTreeManager.getNode(parent.key)
                const parentPath = blockTreeManager.getPath(parent.key, {
                  format: 'string',
                })
                invariant(parentPath, 'parentPath should exist')
                const newBlockId = getId()

                if (parentNode?.value.type === 'list') {
                  va.track('Block created', {
                    source: 'Sidebar',
                    blockType: 'Instance (parent is list)',
                  })
                  ph.capture('Block created', {
                    source: 'Sidebar',
                    blockType: 'Instance (parent is list)',
                  })
                  const params: CreateBlockParams = {
                    data: {
                      id: newBlockId,
                      title: block.title,
                      type: blockTypesRecord.instance,
                      typeOptions: {
                        targetBlockId:
                          parentNode.value.typeOptions.targetBlockId,
                      },
                    },
                    parentInstanceChildTarget:
                      parentNode.value.instanceChildTarget,
                    position: { type: 'first' },
                    isoNow: new Date().toISOString(),
                    path: parentPath,
                    idSeed: getId(),
                  }
                  const result =
                    await data?.replicache?.mutate.createBlock(params)
                  onCreateNewBlock({
                    blockId: newBlockId,
                    path: parentPath + '/' + newBlockId,
                  })
                  if (result?.undo) {
                    undoManager.current?.push({
                      redo: [{ op: 'createBlock', args: params }],
                      undo: [
                        {
                          op: 'blockBatchMutations',
                          args: { mutations: result.undo },
                        },
                      ],
                    })
                  }
                } else {
                  va.track('Block created', {
                    source: 'Sidebar',
                    blockType: 'Document',
                  })
                  ph.capture('Block created', {
                    source: 'Sidebar',
                    blockType: 'Document',
                  })
                  const params: CreateBlockParams = {
                    data: {
                      id: newBlockId,
                      title: block.title,
                      type: block.type,
                    },
                    parentInstanceChildTarget:
                      parentNode?.value.instanceChildTarget,
                    isoNow: new Date().toISOString(),
                    position: { type: 'first' },
                    path: parentPath,
                    idSeed: getId(),
                  }
                  const result =
                    await data?.replicache?.mutate.createBlock(params)
                  onCreateNewBlock({
                    blockId: newBlockId,
                    path: parentPath + '/' + newBlockId,
                  })
                  if (result?.undo) {
                    undoManager.current?.push({
                      redo: [{ op: 'createBlock', args: params }],
                      undo: [
                        {
                          op: 'blockBatchMutations',
                          args: { mutations: result.undo },
                        },
                      ],
                    })
                  }
                }
              }
            : undefined
        }
      >
        <TreePrimitive.TreeTitle
          showActionsOnHover={false}
          nestedItemsQuantity={
            isBlockWithChildren(blockTreeManager.root.value)
              ? (blockTreeManager.getChildrenKeys(blockTreeManager.root.key)
                  ?.length ?? 0)
              : 0
          }
          disableAddBelow={!isMemberOrHigher}
        >
          All blocks
        </TreePrimitive.TreeTitle>
        <TreePrimitive.TreeList
          onFocus={() => {
            // when the user focuses this tree, we want to unselect the other one
            onUnselectFavsTree()
          }}
          papadaSize="large"
          repoUndoStuff={{ undoManager: undoManager.current, encodedSegment }}
        >
          <TreePrimitive.TreeListChild
            id={`docs-tree`}
            parent={blockTreeManager.root}
            renderControllerRef={renderControllerRef}
            getChildrenToRender={(parent, _level, group) => {
              if (isBlockWithChildren(parent.value)) {
                const _children = (
                  blockTreeManager?.getChildrenKeys(parent.key) ?? []
                )
                  .map((ch) => {
                    const node = blockTreeManager.getNode(ch)
                    if (!node) return null
                    return {
                      id: node.value.id,
                      type: node.value.type,
                      hidden: node.value.hidden,
                    }
                  })
                  .filter(Boolean)

                childrenToRenderCache.current.set(parent.key, _children)

                let children = _children.map((ch) => ch.id)
                if (isListBlock(parent.value)) {
                  if (group) {
                    const { selectedView } = getListViewControls(parent.value)
                    const isGroupActive =
                      selectedView === undefined ||
                      selectedView?.id === group.key
                    if (isGroupActive) {
                      const workingTemplate = blockTreeManager.getNode(
                        parent.value.typeOptions.targetBlockId,
                        { required: true }
                      )

                      // Store template IDs for easier reference
                      const templateIds = [
                        workingTemplate.value.id,
                        workingTemplate.value.sourceBlockId,
                      ].filter(Boolean)

                      // Find which block in the active path exists in children
                      const activeBlock = children.find((c) =>
                        activeBlockPath.includes(c)
                      )
                      if (!activeBlock) {
                        return [] // No active block found in children
                      }

                      // First sort to ensure template is at the top if present
                      const sortedChildren = workingTemplate.value.hidden
                        ? [...children].sort((a, b) => {
                            // If a is a template ID, it should come first
                            if (templateIds.includes(a)) return -1
                            // If b is a template ID, it should come first
                            if (templateIds.includes(b)) return 1
                            return 0
                          })
                        : children

                      // Find index of active block in sorted array
                      const activeBlockIndex = sortedChildren.findIndex(
                        (c) => c === activeBlock
                      )

                      // Get prev and next blocks
                      const prev =
                        activeBlockIndex > 0
                          ? sortedChildren[activeBlockIndex - 1]
                          : null
                      const next =
                        activeBlockIndex < sortedChildren.length - 1
                          ? sortedChildren[activeBlockIndex + 1]
                          : null

                      // Create final array with just prev, active, and next blocks
                      const visibleBlocks = [prev, activeBlock, next].filter(
                        Boolean
                      )

                      // Convert IDs to nodes
                      return visibleBlocks
                        .map((c) => {
                          const node = blockTreeManager.getNode(c, {
                            required: true,
                            fallbackKey: 'sourceBlockId',
                          })

                          if (!isBlockWithChildren(node.value)) {
                            return null
                          }

                          return node
                        })
                        .filter(Boolean)
                    } else {
                      children = [] // Not active group, return empty array
                    }
                  }
                }

                return children
                  .map((c) => {
                    const node = blockTreeManager.getNode(c, {
                      required: true,
                      fallbackKey: 'sourceBlockId',
                    })
                    if (!isBlockWithChildren(node.value)) return null
                    return node
                  })
                  .filter(Boolean)
                  .sort((a) => {
                    if (
                      isListBlock(parent.value) &&
                      (parent.value.typeOptions.targetBlockId === a.value.id ||
                        parent.value.typeOptions.targetBlockId ===
                          a.value.sourceBlockId) &&
                      a.value.hidden
                    ) {
                      return -1 // template blocks are always pinned to the top.
                    } else return 0
                  })
              } else {
                childrenToRenderCache.current.set(parent.key, null)
                return null
              }
            }}
            getGroups={(_parent, _level, { isParentActive }) => {
              const parent = { ..._parent }
              if (!isListBlock(parent.value)) return null
              const grandparent = parent.parentKey
                ? blockTreeManager.getNode(parent.parentKey)
                : null

              const hasViews = parent.value.value.views?.length
              const views = hasViews
                ? (parent.value.value.views ?? [DEFAULT_LIST_VIEW])
                : [DEFAULT_LIST_VIEW]

              const { selectedView, setSelectedView } = getListViewControls(
                parent.value
              )

              return views.map((view) => ({
                key: view.id,
                title: view.title || <span>{getViewDefaultTitle(view)}</span>,
                dim:
                  parent.value.hidden || (grandparent?.value.hidden ?? false),
                isActive:
                  isParentActive &&
                  (hasViews ? selectedView?.id === view.id : true),
                onClick: (group, { doubleClick }) => {
                  onActiveKeyChange(idToSourceBlockId(parent.value), {
                    autoFocus: false,
                    partiallyOpen: !doubleClick,
                  })
                  setSelectedView(group.key)
                },
              }))
            }}
            emptyState={(level) => {
              if (level !== 0 || isExternalVisitor) {
                return (
                  <p className={clsx(level === 0 ? '-ml-0.5' : 'ml-2')}>
                    No items
                  </p>
                )
              }

              return (
                <Button
                  unstyled
                  focusStyled
                  className="text-xs rounded-xl border border-dashed border-dark-elevated min-h-[85px] hover:bg-dark-shade bg-dark-base px-4 font-medium text-dark-bright -ml-1.5 -mr-4 w-[calc(100%+22px)] cursor-default mt-1"
                  onClick={async () => {
                    if (!blockTreeManager || !data?.replicache) return

                    const params: CreateBlockParams = {
                      data: {
                        id: getId(),
                        title: '',
                        type: blockTypesRecord.document,
                      },
                      parentInstanceChildTarget:
                        blockTreeManager.root.value.instanceChildTarget,
                      position: { type: 'last' },
                      isoNow: new Date().toISOString(),
                      path: blockTreeManager.root.value.id,
                      idSeed: getId(),
                    }
                    const { newBlock, undo } =
                      await data.replicache.mutate.createBlock(params)

                    if (!newBlock) return
                    setFocus({
                      type: 'block:document-title',
                      blockId: newBlock.id,
                    })
                    addTab({ key: newBlock.id, partiallyOpen: true })
                    if (undo) {
                      undoManager.current?.push({
                        redo: [{ op: 'createBlock', args: params }],
                        undo: [
                          {
                            op: 'blockBatchMutations',
                            args: { mutations: undo },
                          },
                        ],
                      })
                    }
                  }}
                >
                  Create an empty doc
                </Button>
              )
            }}
            level={0}
          >
            {Child}
          </TreePrimitive.TreeListChild>
        </TreePrimitive.TreeList>
      </TreePrimitive.Tree>
    </ChildContext.Provider>
  )
}

const ChildContext = React.createContext<{
  editorMode: EditorMode
  userRole: Role
  activeTabKey: string | undefined
  undoManager: RepoUndoManager | null
  replicache: Replicache<RepoSpaceMutators> | null | undefined
}>({
  editorMode: 'preview',
  userRole: 'viewer',
  activeTabKey: undefined,
  undoManager: null,
  replicache: null,
})

const Child: TreePrimitive.TreeListChildProps<Block>['children'] = ({
  child: _child,
  setIsRenaming,
  selectedKeys,
  isSelected,
}) => {
  const { editorMode, userRole, activeTabKey, undoManager, replicache } =
    React.useContext(ChildContext)
  const treeManager = useWorkingTreeStore((s) => s.tree)
  const node = useWorkingTreeStore((s) => s.tree?.getNode(_child.key))
  const path = useWorkingTreeStore((s) =>
    node
      ? s.tree?.getPath(node.key, { format: 'string', required: true })
      : null
  )
  const parentNode = useWorkingTreeStore((s) =>
    node?.parentKey ? s.tree?.getNode(node?.parentKey) : null
  )

  if (!treeManager || !node || !path) return null

  if (!isBlockWithChildren(node.value)) {
    return null
  }

  const { displayTitle } = getBlockDisplayData({
    block: node.value,
    parentBlock: parentNode?.value,
    ignoreDisplayTitle: true,
  })
  const highlight = isComponentBlock(node.value)
  const dim = node.value.hidden || parentNode?.value.hidden

  const allowedMenuActions = getAllowedMenuActions({
    block: node.value,
    editorMode,
    parentBlock: parentNode?.value,
    userRole,
    path,
    treeManager: treeManager,
  })

  const focusOp = {
    op: 'focus',
    args: {
      focus: {
        blockId: idToSourceBlockId(node.value),
        type: 'block:sidebar',
      },
      preferredTabKey: activeTabKey,
    },
  } as const

  return (
    <TreePrimitive.TreeListItem
      contextMenuActions={
        <ContextMenuActions
          blockTreeManager={treeManager}
          child={node}
          setIsRenaming={setIsRenaming}
          selectedKeys={selectedKeys}
          isSelected={isSelected}
          canDuplicate
        />
      }
      draggable={allowedMenuActions.reorder}
      rename={allowedMenuActions.rename ? 'enabled' : 'disabled'}
    >
      <TreePrimitive.TreeListItemChevron
        saveExpandedStateInStorage={{
          type: 'sessionStorage',
          key: idToSourceBlockId(node.value),
        }}
      />
      <TreePrimitive.TreeListItemIcon
        className={clsx(
          highlight && `text-dark-control-highlight`,
          dim && (highlight ? 'opacity-70' : 'opacity-50')
        )}
      >
        <BlockIcon block={node.value} />
      </TreePrimitive.TreeListItemIcon>
      <TreePrimitive.TreeListItemTitle
        className={clsx(
          highlight && `text-dark-control-highlight`,
          dim && (highlight ? 'opacity-70' : 'opacity-50')
        )}
        rename={allowedMenuActions.rename ? 'enabled' : 'disabled'}
      >
        {displayTitle}
      </TreePrimitive.TreeListItemTitle>

      <TreePrimitive.TreeListItemRenameInput
        defaultValue={node.value.title}
        onRename={async (value) => {
          const params: UpdateBlockParams = {
            id: node.value.id,
            data: {
              type: node.value.type,
              title: value,
            },
            instanceChildTarget: node.value.instanceChildTarget,
            strategy: 'merge',
            path: treeManager.getPath(node.key, {
              format: 'string',
              required: true,
            }),
            isoNow: new Date().toISOString(),
          }
          const result = await replicache?.mutate.updateBlock(params)
          if (result?.undo) {
            undoManager?.push({
              redo: [focusOp, { op: 'updateBlock', args: params }],
              undo: [
                focusOp,
                {
                  op: 'blockBatchMutations',
                  args: { mutations: result.undo },
                },
              ],
            })
          }
        }}
      />

      <TreePrimitive.TreeListItemActions
        plusButtonOptions={{
          variant:
            allowedMenuActions['add-below'] && node?.value.type !== 'reference'
              ? 'enabled-pre'
              : 'disabled',
          dimmed: Boolean(node.value.hidden || parentNode?.value.hidden),
        }}
      >
        {
          // don't show hidden icon if it's inheriting state from parent
          !parentNode?.value.hidden &&
            (allowedMenuActions['toggle-hidden'] ? (
              <TreePrimitive.TreeListItemAction
                ariaLabel={node.value.hidden ? 'Show' : 'Hide'}
                icon={
                  node.value.hidden ? (
                    <EyeClosedIcon className="opacity-50" />
                  ) : (
                    <EyeOpenIcon />
                  )
                }
                onClick={async (e) => {
                  const params: UpdateBlockParams = {
                    id: node.value.id,
                    data: {
                      hidden: !node.value.hidden,
                      type: node.value.type,
                    },
                    instanceChildTarget: node.value.instanceChildTarget,
                    skipPropagateHiddenState: !!node.value.hidden && e.shiftKey,
                    strategy: 'merge',
                    path: treeManager.getPath(node.key, {
                      format: 'string',
                      required: true,
                    }),
                    isoNow: new Date().toISOString(),
                  }
                  const result = await replicache?.mutate.updateBlock(params)
                  if (result?.undo) {
                    undoManager?.push({
                      redo: [focusOp, { op: 'updateBlock', args: params }],
                      undo: [
                        focusOp,
                        {
                          op: 'blockBatchMutations',
                          args: { mutations: result.undo },
                        },
                      ],
                    })
                  }
                }}
                alwaysVisible={node.value.hidden}
              />
            ) : // Show closed eye icon if block is hidden and user cannot toggle it.
            userRole !== 'viewer' && node.value.hidden ? (
              <TreePrimitive.TreeListItemAction
                className="pointer-events-none"
                icon={<EyeClosedIcon className="opacity-50 text-dark-muted" />}
                ariaLabel="Hidden"
                alwaysVisible
              />
            ) : null)
        }
      </TreePrimitive.TreeListItemActions>
    </TreePrimitive.TreeListItem>
  )
}
